/**
 * Transcript chunk lookup filter helpers — map UI state to Brooklyn `TranscriptChunkSearchFilter`
 * and `contentList` typeahead queries (ported from gaia-web-app semantic-stitch filters + person scope).
 */

export const FILTER_SCOPE_ANY = 'ANY'
export const FILTER_SCOPE_VIDEO = 'VIDEO'
export const FILTER_SCOPE_SERIES = 'SERIES'
export const FILTER_SCOPE_CENTER = 'CENTER'
export const FILTER_SCOPE_PERSON = 'PERSON'

export const FILTER_SCOPE_OPTIONS = [
  { value: FILTER_SCOPE_ANY, label: 'Any content' },
  { value: FILTER_SCOPE_VIDEO, label: 'A video' },
  { value: FILTER_SCOPE_SERIES, label: 'A series' },
  { value: FILTER_SCOPE_CENTER, label: 'A center' },
  { value: FILTER_SCOPE_PERSON, label: 'A person' },
] as const

export const PUBLISHED_AGE_OPTIONS = [
  { value: 'ANY', label: 'Any publish date' },
  { value: 'PAST_MONTH', label: 'Past month' },
  { value: 'PAST_3_MONTHS', label: 'Past 3 months' },
  { value: 'PAST_6_MONTHS', label: 'Past 6 months' },
  { value: 'PAST_YEAR', label: 'Past year' },
  { value: 'PAST_2_YEARS', label: 'Past 2 years' },
  { value: 'PAST_3_YEARS', label: 'Past 3 years' },
  { value: 'PAST_5_YEARS', label: 'Past 5 years' },
] as const

export const MAX_SEASONS = 20
export const TYPEAHEAD_DEBOUNCE_MS = 300
export const TYPEAHEAD_LIMIT = 50

export type FilterScope = (typeof FILTER_SCOPE_OPTIONS)[number]['value']
export type PublishedAge = (typeof PUBLISHED_AGE_OPTIONS)[number]['value']

export type TypeaheadItem = {
  contentId: number
  title?: string | null
  contentType?: string | null
  bundle?: string | null
}

export function isFilterScopeIncomplete ({
  filterScope,
  filterItem,
}: {
  filterScope: FilterScope
  filterItem: TypeaheadItem | null
}): boolean {
  return filterScope !== FILTER_SCOPE_ANY && filterItem == null
}

export function hasActiveFilterState ({
  filterScope,
  filterItem,
  filterPublishedAge,
}: {
  filterScope: FilterScope
  filterItem: TypeaheadItem | null
  filterPublishedAge: PublishedAge
}): boolean {
  return (filterScope !== FILTER_SCOPE_ANY && filterItem != null) || filterPublishedAge !== 'ANY'
}

export function buildSearchFilter ({
  filterScope,
  filterItem,
  filterSeason,
  filterPublishedAge,
}: {
  filterScope: FilterScope
  filterItem: TypeaheadItem | null
  filterSeason: number | null
  filterPublishedAge: PublishedAge
}) {
  const hasScope = filterScope !== FILTER_SCOPE_ANY && filterItem != null
  const hasDate = filterPublishedAge !== 'ANY'

  if (!hasScope && !hasDate) {
    return null
  }

  const base = hasDate ? { publishedAge: filterPublishedAge } : {}

  if (filterScope === FILTER_SCOPE_VIDEO) {
    return { ...base, contentId: filterItem!.contentId }
  }
  if (filterScope === FILTER_SCOPE_SERIES) {
    return {
      ...base,
      seriesContentId: filterItem!.contentId,
      seasonNumber: filterSeason != null ? filterSeason : undefined,
    }
  }
  if (filterScope === FILTER_SCOPE_CENTER) {
    return { ...base, centerContentId: filterItem!.contentId }
  }
  if (filterScope === FILTER_SCOPE_PERSON) {
    return { ...base, personContentId: filterItem!.contentId }
  }
  return hasDate ? base : null
}

export function buildTypeaheadWhere (scope: FilterScope, term: string) {
  const titleFilter = term
    ? { title: { ilike: `%${term.split(/\s+/u).join('%')}%` } }
    : { title: { ilike: '%' } }

  if (scope === FILTER_SCOPE_VIDEO) {
    return { ...titleFilter, contentType: { eq: 'video' } }
  }
  if (scope === FILTER_SCOPE_SERIES) {
    return { ...titleFilter, contentType: { eq: 'series' } }
  }
  if (scope === FILTER_SCOPE_CENTER) {
    return { ...titleFilter, contentType: { eq: 'tag' }, bundle: { eq: 'centers' } }
  }
  if (scope === FILTER_SCOPE_PERSON) {
    return { ...titleFilter, contentType: { eq: 'person' }, bundle: { eq: 'people' } }
  }
  return null
}
